import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Drug APIs
export const drugAPI = {
  search: (query, limit = 10, skip = 0) => 
    api.get(`/drugs/search?q=${encodeURIComponent(query)}&limit=${limit}&skip=${skip}`),
  
  getByApplicationNumber: (appNumber) => 
    api.get(`/drugs/${appNumber}`),
  
  getAdverseEvents: (drugName, limit = 10) => 
    api.get(`/drugs/${encodeURIComponent(drugName)}/adverse-events?limit=${limit}`),
  
  getLabels: (drugName, limit = 5) => 
    api.get(`/drugs/${encodeURIComponent(drugName)}/labels?limit=${limit}`),
  
  getAllFromDB: (limit = 50, offset = 0) => 
    api.get(`/drugs/db/all?limit=${limit}&offset=${offset}`)
};

// Clinical Trials APIs
export const trialsAPI = {
  search: (query, pageSize = 10, pageToken = null) => {
    const params = new URLSearchParams({ q: query, pageSize });
    if (pageToken) params.append('pageToken', pageToken);
    return api.get(`/trials/search?${params}`);
  },
  
  getByNctId: (nctId) => 
    api.get(`/trials/${nctId}`),
  
  searchByCondition: (condition, pageSize = 10) => 
    api.get(`/trials/condition/${encodeURIComponent(condition)}?pageSize=${pageSize}`),
  
  searchByIntervention: (intervention, pageSize = 10) => 
    api.get(`/trials/intervention/${encodeURIComponent(intervention)}?pageSize=${pageSize}`),
  
  getAllFromDB: (limit = 50, offset = 0) => 
    api.get(`/trials/db/all?limit=${limit}&offset=${offset}`)
};

// Chemicals APIs
export const chemicalsAPI = {
  search: (query, source = 'both', limit = 10) => 
    api.get(`/chemicals/search?q=${encodeURIComponent(query)}&source=${source}&limit=${limit}`),
  
  getChEBI: (chebiId) => 
    api.get(`/chemicals/chebi/${chebiId}`),
  
  getChEMBL: (chemblId) => 
    api.get(`/chemicals/chembl/${chemblId}`),
  
  getActivities: (chemblId, limit = 20) => 
    api.get(`/chemicals/chembl/${chemblId}/activities?limit=${limit}`),
  
  searchBySmiles: (smiles, similarity = 70) => 
    api.get(`/chemicals/smiles?smiles=${encodeURIComponent(smiles)}&similarity=${similarity}`),
  
  searchDrug: (drugName, limit = 10) => 
    api.get(`/chemicals/drug/${encodeURIComponent(drugName)}?limit=${limit}`),
  
  getAllFromDB: (limit = 50, offset = 0) => 
    api.get(`/chemicals/db/all?limit=${limit}&offset=${offset}`)
};

// Orange Book APIs
export const orangeBookAPI = {
  search: (query, type = 'both', limit = 10) => 
    api.get(`/orangebook/search?q=${encodeURIComponent(query)}&type=${type}&limit=${limit}`),
  
  searchByIngredient: (ingredient, limit = 10) => 
    api.get(`/orangebook/ingredient/${encodeURIComponent(ingredient)}?limit=${limit}`),
  
  searchByBrand: (brandName, limit = 10) => 
    api.get(`/orangebook/brand/${encodeURIComponent(brandName)}?limit=${limit}`),
  
  getTherapeuticEquivalence: (appNumber) => 
    api.get(`/orangebook/te/${appNumber}`),
  
  searchGenerics: (query, limit = 20) => 
    api.get(`/orangebook/generics/${encodeURIComponent(query)}?limit=${limit}`),
  
  getAllFromDB: (limit = 50, offset = 0) => 
    api.get(`/orangebook/db/all?limit=${limit}&offset=${offset}`)
};

// Auth APIs
export const authAPI = {
  register: (userData) => 
    api.post('/auth/register', userData),
  
  login: (credentials) => 
    api.post('/auth/login', credentials),
  
  verify: () => 
    api.get('/auth/verify')
};

// PubChem APIs
export const pubchemAPI = {
  search: (query, limit = 10) => 
    api.get(`/pubchem/search?q=${encodeURIComponent(query)}&limit=${limit}`),
  
  getCompound: (cid) => 
    api.get(`/pubchem/compound/${cid}`),
  
  getInteractions: (drugName) => 
    api.get(`/pubchem/interactions?drug=${encodeURIComponent(drugName)}`)
};

// RxNorm APIs
export const rxnormAPI = {
  search: (query) => 
    api.get(`/rxnorm/search?q=${encodeURIComponent(query)}`),
  
  getDrug: (rxcui) => 
    api.get(`/rxnorm/${rxcui}`),
  
  getInteractions: (rxcui) => 
    api.get(`/rxnorm/interactions/${rxcui}`),
  
  getRelated: (rxcui) => 
    api.get(`/rxnorm/related/${rxcui}`),
  
  getSpelling: (query) => 
    api.get(`/rxnorm/spelling?q=${encodeURIComponent(query)}`)
};

// DailyMed APIs
export const dailymedAPI = {
  search: (query, pageSize = 10, page = 1) => 
    api.get(`/dailymed/search?q=${encodeURIComponent(query)}&pageSize=${pageSize}&page=${page}`),
  
  getLabel: (setid) => 
    api.get(`/dailymed/label/${setid}`),
  
  searchByNDC: (ndc) => 
    api.get(`/dailymed/ndc?ndc=${ndc}`),
  
  getImages: (setid) => 
    api.get(`/dailymed/images/${setid}`)
};

export default api;